import React from 'react'

import Head from 'next/head'

import ClientErrorBoundary from '@/components/error/ClientErrorBoundary'
import { getLayout } from '@/components/layouts/recipient/Layout'
import RecipientSearch from '@/components/recipient-search/RecipientSearch'
import { getMetadataFn } from '@/components/seo/DefaultMetadata'

import { requireAuth } from '@/helpers/auth'

import { useRecipientSearchUrl } from '@/hooks/recipient-search'

import { useRecipientSearchStore } from '@/stores/RecipientSearchStore'

import type { NextPageWithLayout } from '@/types/next'

/**
 * Recipient Search Page
 *
 * Public search interface for federal award recipients.
 * Supports:
 * - Text search (name, UEI, DUNS)
 * - Certification filters (8(a), WOSB, SDVOSB, HUBZone, etc.)
 * - Location filters (state, city)
 * - Industry filters (NAICS, PSC)
 * - Financial filters (spending range, award count)
 * - Parent/subsidiary navigation
 *
 * Filter state is persisted in URL query parameters for:
 * - Shareable links
 * - Browser back/forward navigation
 * - Persistence across page refreshes
 *
 * Designed for contractors, analysts, reporters, and anyone
 * researching federal award recipients.
 */
const RecipientSearchPage: NextPageWithLayout = () => {
  // Sync filter state with URL query parameters
  useRecipientSearchUrl()

  // Subscribe to displayQuery for instant title updates (no debounce delay)
  const displayQuery = useRecipientSearchStore((s) => s.displayQuery).trim()
  const pageTitle = displayQuery
    ? `${displayQuery} - Recipient Search - Sweetspot`
    : 'Recipient Search - Sweetspot'

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <ClientErrorBoundary>
        <RecipientSearch />
      </ClientErrorBoundary>
    </>
  )
}

RecipientSearchPage.getLayout = getLayout
RecipientSearchPage.getMetadata = getMetadataFn({
  title: 'Federal Award Recipients - Sweetspot',
  description:
    'Search and explore federal award recipients. Find contractors by name, certifications, industry, and award history. Research recipient data for government contracting insights.',
})

export default requireAuth(RecipientSearchPage)
